import {useMemo} from 'react';
import assert from 'assert-ts';
import type {AgentSubType} from 'types';
import type {AgentWorkSummary} from 'services/agent/types';
import type {DataLoadStatus} from 'api';
import type {DeleteAgentFunc, SaveAgentFunc} from 'services/agent';
import {useAgent} from 'services/agent';
import type {AgentState} from 'services/agent/reducer/agentReducer';
import type {AgentIdParam, NewAgentParam} from '../types';

export const useNewOrExistingAgent = ({
  agentId,
  agentType,
  suggestion,
}: NewAgentParam | AgentIdParam):
  | {
      /** Loaded agent, with limit works data */
      agent: AgentState;
      /** Loaded work summaries, possibly incremental (batched) */
      workSummaries?: DataLoadStatus<AgentWorkSummary[]>;
      agentType?: undefined;
      suggestion?: undefined;
      saveAgent: SaveAgentFunc;
      deleteAgent: DeleteAgentFunc;
    }
  | {
      agent?: undefined;
      workSummaries?: undefined;
      suggestion: string;
      agentType: AgentSubType;
      saveAgent: SaveAgentFunc;
      deleteAgent: DeleteAgentFunc;
    } => {
  const {
    agentEmptyWorks: existingAgent,
    workSummaries,
    saveAgent,
    deleteAgent,
  } = useAgent(agentId, 'full', 'mentionedIn');

  return useMemo(() => {
    if (agentId) {
      return {agent: existingAgent, workSummaries, saveAgent, deleteAgent};
    }

    return {
      agentType: assert(agentType, 'agentType is required'),
      workSummaries: undefined,
      suggestion: suggestion ?? '',
      saveAgent,
      deleteAgent,
    };
  }, [
    agentId,
    agentType,
    deleteAgent,
    existingAgent,
    saveAgent,
    suggestion,
    workSummaries,
  ]);
};
